/**
 * WishListManager Component
 * Community-driven widget request management system
 * Redesigned to match professional FeedbackManager presentation
 * Updated: January 2025
 */

import React, { useState, useEffect } from 'react';
import './FeedbackManager.css'; // Reuse existing styles
import WidgetVersionControl from './WidgetVersionControl';
import EmailModal from './EmailModal';

const WishListManager = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    total_requests: 0,
    unique_users: 0,
    pending_count: 0,
    processed_count: 0,
    approved_count: 0,
    rejected_count: 0,
    in_development: 0,
    completed: 0,
    avg_priority_score: 0,
    categories: []
  });
  
  // Filter and UI state
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortBy, setSortBy] = useState('priority');
  const [expandedRequest, setExpandedRequest] = useState(null);
  const [pendingEdits, setPendingEdits] = useState({}); // Track pending item edits
  const [processingCBA, setProcessingCBA] = useState(null); // Track CBA processing state
  const [voteStats, setVoteStats] = useState({}); // Track vote statistics for each request
  
  // Email modal state
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedWidgetRequest, setSelectedWidgetRequest] = useState(null);

  // Fetch widget requests
  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && ['pending', 'processed', 'approved', 'rejected'].includes(statusFilter)) {
        params.set('status', statusFilter);
      }
      if (categoryFilter) {
        params.set('category', categoryFilter);
      }
      const qs = params.toString();
      const response = await fetch(`/api/widget-requests/admin/priority${qs ? `?${qs}` : ''}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch requests: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        const requests = data.requests || [];
        setRequests(requests);
        setError('');
        
        // Fetch vote statistics for all requests
        const requestIds = requests.map(req => req.id);
        if (requestIds.length > 0) {
          fetchVoteStats(requestIds);
        }
      } else {
        throw new Error(data.error || 'Failed to fetch requests');
      }
    } catch (err) {
      console.error('Error fetching widget requests:', err);
      setError(err.message);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch statistics
  const fetchStats = async () => {
    try {
      const response = await fetch('/api/widget-requests/admin/stats');
      
      if (!response.ok) {
        throw new Error(`Failed to fetch stats: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Error fetching widget request stats:', err);
    }
  };

  // Fetch vote statistics for all requests
  const fetchVoteStats = async (requestIds) => {
    try {
      console.log('[VOTE STATS] Fetching vote stats for requests:', requestIds);
      
      const voteStatsPromises = requestIds.map(async (requestId) => {
        const url = `/api/widget-requests/vote-stats/${requestId}`;
        console.log('[VOTE STATS] Fetching:', url);
        
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          console.log('[VOTE STATS] Response for request', requestId, ':', data);
          if (data.success) {
            return { requestId, stats: data.vote_stats };
          }
        } else {
          console.error('[VOTE STATS] Failed to fetch for request', requestId, ':', response.status);
        }
        return { requestId, stats: { upvotes: 0, downvotes: 0, net_score: 0, total_votes: 0 } };
      });

      const results = await Promise.all(voteStatsPromises);
      const voteStatsMap = {};
      results.forEach(({ requestId, stats }) => {
        voteStatsMap[requestId] = stats;
      });
      
      console.log('[VOTE STATS] Final vote stats map:', voteStatsMap);
      setVoteStats(voteStatsMap);
    } catch (err) {
      console.error('Error fetching vote statistics:', err);
    }
  };

  // Perform admin actions
  const handleAction = async (requestId, action, additionalData = {}) => {
    try {
      // Check if this is a CBA approval action
      const isCBAAction = action === 'approve' && additionalData.implementation_status === 'in_progress';
      
      if (isCBAAction) {
        setProcessingCBA(requestId);
      }

      const response = await fetch('/api/widget-requests/admin/action', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          request_id: requestId,
          action: action,
          ...additionalData
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to perform action: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Clear CBA processing state
        if (isCBAAction) {
          setProcessingCBA(null);
        }
        
        // Refresh the data
        fetchRequests();
        fetchStats();
        alert(`Action "${action}" completed successfully!`);
      } else {
        throw new Error(data.error || 'Failed to perform action');
      }
    } catch (err) {
      console.error(`Error performing action ${action}:`, err);
      // Clear CBA processing state on error
      setProcessingCBA(null);
      alert(`Error performing action: ${err.message}`);
    }
  };

  // Handle priority adjustment
  const handlePriorityAdjust = async (requestId, adjustment) => {
    try {
      // Find the current item
      const currentItem = requests.find(item => item.id === requestId);
      if (!currentItem) return;

      const currentAdminAdjustment = currentItem.admin_priority_adjustment || 0;
      let newAdminAdjustment;

      // Implement proper increment/decrement logic
      if (adjustment > 0) {
        // Plus button: 0 -> +0.01, -0.01 -> 0
        if (currentAdminAdjustment === 0) {
          newAdminAdjustment = 0.01;
        } else if (currentAdminAdjustment === -0.01) {
          newAdminAdjustment = 0;
        } else {
          newAdminAdjustment = Math.round((currentAdminAdjustment + 0.01) * 100) / 100;
        }
      } else {
        // Minus button: 0 -> -0.01, +0.01 -> 0
        if (currentAdminAdjustment === 0) {
          newAdminAdjustment = -0.01;
        } else if (currentAdminAdjustment === 0.01) {
          newAdminAdjustment = 0;
        } else {
          newAdminAdjustment = Math.round((currentAdminAdjustment - 0.01) * 100) / 100;
        }
      }

      // Calculate what the new total priority would be
      const baseScore = currentItem.group_user_count ? 
        (currentItem.group_user_count || 1) + (currentItem.group_avg_credibility || 0) :
        1 + (currentItem.user_reputation || 0);
      const communityVotes = (currentItem.community_net_votes || 0);
      const newPriority = baseScore + communityVotes + newAdminAdjustment;
      
      // Prevent negative priorities
      if (newPriority < 0) {
        alert('Priority cannot be negative');
        return;
      }

      const response = await fetch('/api/widget-requests/admin/action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_id: requestId,
          action: 'adjust_priority', // Use adjust_priority action to avoid changing processing status
          admin_notes: `Admin priority adjustment: ${newAdminAdjustment >= 0 ? '+' : ''}${newAdminAdjustment.toFixed(2)}`,
          priority_adjustment: newAdminAdjustment
        })
      });

      const data = await response.json();
      if (data.success) {
        // Update the local state immediately for better UX
        setRequests(prev => prev.map(item => 
          item.id === requestId 
            ? { ...item, priority_score: newPriority, admin_priority_adjustment: newAdminAdjustment }
            : item
        ));
        
        fetchStats(); // Refresh stats
        fetchVoteStats([requestId]); // Refresh vote stats for this item
      } else {
        console.error('Failed to adjust priority:', data.error);
        alert('Failed to adjust priority: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error adjusting priority:', error);
      alert('Error adjusting priority. Please try again.');
    }
  };

  // Handle pending actions (process/reject with edits)
  const handlePendingAction = async (requestId, action) => {
    try {
      const edits = pendingEdits[requestId] || {};
      const originalItem = requests.find(r => r.id === requestId);
      
      // Prepare admin edits with categories support
      const selectedCategories = edits.categories || [originalItem?.category].filter(Boolean);
      const adminEdits = {
        ...edits,
        // Send primary category for backend compatibility
        category: selectedCategories[0] || originalItem?.category,
        // Also send all categories for future enhancement
        all_categories: selectedCategories
      };
      
      const response = await fetch('/api/widget-requests/admin/action', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_id: requestId,
          action: action === 'process' ? 'process' : 'reject',
          admin_user_id: 'admin',
          admin_edits: adminEdits,
          admin_notes: action === 'process' ? 
            (edits.admin_notes || `Admin processed with ${adminEdits.all_categories.length} categories`) : 
            (edits.admin_notes || 'Admin rejected during review')
        })
      });

      const data = await response.json();
      if (data.success) {
        // Clear edits for this item
        setPendingEdits(prev => {
          const newEdits = { ...prev };
          delete newEdits[requestId];
          return newEdits;
        });
        
        // Refresh the data
        fetchRequests();
        fetchStats();
        alert(action === 'process' ? 
          `Request processed successfully with ${adminEdits.all_categories.length} categories!` : 
          'Request rejected successfully!');
      } else {
        console.error('Failed to process pending action:', data.error);
        alert('Failed to process action: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error processing pending action:', error);
      alert('Error processing action. Please try again.');
    }
  };

  // Handle simple approve action (for backward compatibility)
  const handleApprove = (requestId) => {
    handlePendingAction(requestId, 'approve');
  };

  // Handle simple reject action (for backward compatibility)
  const handleReject = (requestId) => {
    handlePendingAction(requestId, 'reject');
  };

  // Handle delete forever with confirmation
  const handleDeleteForever = (requestId) => {
    if (window.confirm('Are you sure you want to permanently delete this request? This action cannot be undone.')) {
      handleAction(requestId, 'delete_forever', {
        admin_notes: 'Deleted by admin'
      });
    }
  };

  // Handle email widget proposer
  const handleEmailProposer = (request) => {
    setSelectedWidgetRequest(request);
    setEmailModalOpen(true);
  };

  // Get priority badge color
  const getPriorityColor = (score) => {
    if (score >= 3) return '#ff6b6b'; // High priority - red
    if (score >= 2) return '#ffa726'; // Medium priority - orange
    if (score >= 1) return '#66bb6a'; // Low priority - green
    return '#90a4ae'; // Very low priority - gray
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Extract update notes from admin_notes field
  const getUpdateNotes = (adminNotes) => {
    if (!adminNotes) return '';
    const updateSection = adminNotes.split('--- Development Updates ---');
    return updateSection.length > 1 ? updateSection[1].trim() : '';
  };

  // Get clean admin notes without update notes
  const getCleanAdminNotes = (adminNotes) => {
    if (!adminNotes) return '';
    const updateSection = adminNotes.split('--- Development Updates ---');
    return updateSection[0].trim();
  };

  // Categories for filtering
  const categories = [
    { id: '', name: 'All Categories' },
    { id: 'financial', name: 'Financial Tools' },
    { id: 'productivity', name: 'Productivity' },
    { id: 'government', name: 'Government Services' },
    { id: 'communication', name: 'Communication' },
    { id: 'analytics', name: 'Analytics & Reports' },
    { id: 'lifestyle', name: 'Lifestyle & Wellness' },
    { id: 'entertainment', name: 'Entertainment' },
    { id: 'other', name: 'Other Ideas' }
  ];

  // Available categories for editing (without "All Categories")
  const editableCategories = categories.filter(cat => cat.id !== '');

  /** Coerce API numbers (PG numeric / bigint JSON strings) for .toFixed() and math */
  const n = (v, fallback = 0) => {
    const x = Number(v);
    return Number.isFinite(x) ? x : fallback;
  };

  // Handle category changes for pending items
  const handleCategoryChange = (requestId, newCategory) => {
    setPendingEdits(prev => {
      const currentEdits = prev[requestId] || {};
      const originalItem = requests.find(r => r.id === requestId);
      const currentCategories = currentEdits.categories || [originalItem?.category].filter(Boolean);
      
      // Toggle selection - add if not present, remove if present
      let updatedCategories;
      if (currentCategories.includes(newCategory)) {
        // Remove category if already selected
        updatedCategories = currentCategories.filter(cat => cat !== newCategory);
      } else {
        // Add category if not selected
        updatedCategories = [...currentCategories, newCategory];
      }
      
      return {
        ...prev,
        [requestId]: {
          ...currentEdits,
          categories: updatedCategories,
          // Keep the primary category as the first one for backward compatibility
          category: updatedCategories[0] || originalItem?.category
        }
      };
    });
  };

  // Get selected categories for display
  const getSelectedCategories = (item) => {
    const edits = pendingEdits[item.id];
    if (edits && edits.categories) {
      return edits.categories;
    }
    return [item.category].filter(Boolean);
  };

  // Check if a category is selected
  const isCategorySelected = (requestId, category) => {
    const edits = pendingEdits[requestId];
    if (edits && edits.categories) {
      return edits.categories.includes(category);
    }
    const originalItem = requests.find(r => r.id === requestId);
    return originalItem?.category === category;
  };

  // Handle text field changes for pending items
  const handleFieldChange = (requestId, field, value) => {
    setPendingEdits(prev => ({
      ...prev,
      [requestId]: {
        ...prev[requestId],
        [field]: value
      }
    }));
  };

  // Get category display name
  const getCategoryDisplayName = (category) => {
    if (!category) return 'Other';
    const cat = categories.find(c => c.id === category);
    return cat ? cat.name : category.replace('_', ' ').toUpperCase();
  };

  // Load data on component mount and when filters change
  useEffect(() => {
    fetchRequests();
    fetchStats();
  }, [statusFilter, categoryFilter]);

  // Filter and sort requests based on selected criteria
  const sortedRequests = React.useMemo(() => {
    // First filter by status
    let filtered = [...requests];
    
    if (statusFilter && statusFilter !== '') {
      filtered = filtered.filter(request => {
        switch (statusFilter) {
          case 'pending':
            return request.processing_status === 'pending';
          case 'processed':
            return request.processing_status === 'processed';
          case 'approved':
            return request.processing_status === 'approved' && !request.implementation_status;
          case 'in_development':
            return request.processing_status === 'approved' && request.implementation_status === 'in_progress';
          case 'completed':
            return request.processing_status === 'approved' && request.implementation_status === 'completed';
          case 'rejected':
            return request.processing_status === 'rejected';
          default:
            return true;
        }
      });
    }
    
    // Then sort the filtered results
    switch (sortBy) {
      case 'priority':
        return filtered.sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0));
      case 'date':
        return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'category':
        return filtered.sort((a, b) => (a.category || '').localeCompare(b.category || ''));
      case 'users':
        return filtered.sort((a, b) => (b.group_user_count || 0) - (a.group_user_count || 0));
      default:
        return filtered;
    }
  }, [requests, sortBy, statusFilter]);

  // Dynamic stats title based on current filters
  const getStatsTitle = () => {
    const hasCategory = categoryFilter && categoryFilter !== '';
    const hasStatus = statusFilter && statusFilter !== '' && statusFilter !== 'all';
    
    if (hasCategory && hasStatus) {
      const statusName = statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1);
      const categoryName = categories.find(c => c.id === categoryFilter)?.name || categoryFilter;
      return `${statusName} ${categoryName}`;
    } else if (hasStatus) {
      return statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1) + ' Widget Requests';
    } else if (hasCategory) {
      return categories.find(c => c.id === categoryFilter)?.name || categoryFilter;
    } else {
      return 'All Categories';
    }
  };

  // Dynamic stats subtitle based on current filters
  const getStatsSubtitle = () => {
    const hasCategory = categoryFilter && categoryFilter !== '';
    const hasStatus = statusFilter && statusFilter !== '' && statusFilter !== 'all';
    
    if (hasCategory && hasStatus) {
      const categoryName = categories.find(c => c.id === categoryFilter)?.name || categoryFilter;
      return `${statusFilter} requests in ${categoryName.toLowerCase()} category only`;
    } else if (hasStatus) {
      return `All ${statusFilter} requests across all categories`;
    } else if (hasCategory) {
      const categoryName = categories.find(c => c.id === categoryFilter)?.name || categoryFilter;
      return `All statuses in ${categoryName.toLowerCase()} category`;
    } else {
      return 'All categories combined';
    }
  };

  if (loading) {
    return (
      <div className="feedback-loading">
        <div className="loading-spinner"></div>
        <p>Loading widget request data...</p>
      </div>
    );
  }

  // Tactical aggregates derived from stats (Co-Lab queue health)
  const wlTotal = Number(stats?.total_requests || 0);
  const wlPending = Number(stats?.pending_count || 0);
  const wlProcessed = Number(stats?.processed_count || 0);
  const wlInDev = Number(stats?.in_development_count || stats?.in_development || 0);
  const wlCompleted = Number(stats?.completed_count || stats?.completed || 0);
  const wlRejected = Number(stats?.rejected_count || 0);
  const wlUniqueUsers = Number(stats?.unique_users || 0);
  const wlAvgPriority = Number(stats?.avg_priority_score || 0);
  const wlPendingPct = wlTotal ? (wlPending / wlTotal) * 100 : 0;
  const wlPriorityPct = Math.min(100, (wlAvgPriority / 10) * 100);
  const wlPriorityLevel = wlAvgPriority >= 7 ? 'critical' : wlAvgPriority >= 4 ? 'warning' : 'good';
  const wlPendingLevel = wlPendingPct >= 60 ? 'critical' : wlPendingPct >= 30 ? 'warning' : 'good';
  const wlLevel = (() => {
    if (wlTotal === 0) return 'good';
    if (wlPending >= 30 || wlPendingPct >= 60) return 'critical';
    if (wlPending >= 10 || wlPendingPct >= 30) return 'warning';
    return 'good';
  })();
  const wlLabel = {
    good: 'CO-LAB QUEUE HEALTHY',
    warning: 'BACKLOG BUILDING',
    critical: 'BACKLOG CRITICAL',
  }[wlLevel];

  return (
    <div className="feedback-manager opo-tactical wishlist-tactical">
      {/* CBA Processing Animation */}
      {processingCBA && (
        <div className="cba-processing-overlay">
          <div className="cba-processing-modal">
            <div className="cba-processing-content">
              <div className="optiprime-logo-simple">
                <span className="logo-text">🚀 OptiPrime</span>
                <div className="processing-dots-simple">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Tactical hero + KPI strip ─────────────────────────── */}
      {stats && (
        <div className="feedback-tactical-top wishlist-tactical-top">
          <div className={`opo-hero opo-hero-${wlLevel}`}>
            <div className="opo-hero-scan" aria-hidden="true" />
            <div className="opo-hero-left">
              <div className="opo-hero-eyebrow">CO-LAB OPERATIONS</div>
              <div className="opo-hero-title">
                <span className={`opo-hero-pulse opo-pulse-${wlLevel}`} />
                <h2>{wlLabel}</h2>
              </div>
              <div className="opo-hero-sub">
                {wlTotal} request{wlTotal === 1 ? '' : 's'} · {wlPending} pending · {wlInDev} in development · {wlCompleted} completed
              </div>
            </div>
            <div className="opo-hero-right">
              <button
                type="button"
                className="opo-refresh-btn"
                onClick={() => { fetchRequests(); fetchStats(); }}
                title="Re-fetch widget requests & stats"
              >
                <span className="opo-refresh-icon" /> SYNC
              </button>
              <div className="opo-hero-timestamp">
                {new Date().toLocaleString()}
              </div>
            </div>
          </div>

          <div className="opo-kpi-row">
            <div className="opo-kpi">
              <div className="opo-kpi-label">TOTAL REQUESTS</div>
              <div className="opo-kpi-value opo-mono-value">{wlTotal.toLocaleString()}</div>
              <div className="opo-kpi-foot">community-submitted ideas</div>
            </div>
            <div className="opo-kpi">
              <div className="opo-kpi-label">UNIQUE USERS</div>
              <div className="opo-kpi-value opo-mono-value opo-level-good">{wlUniqueUsers.toLocaleString()}</div>
              <div className="opo-kpi-foot">{(wlUniqueUsers > 0 ? wlTotal / wlUniqueUsers : 0).toFixed(1)} avg / user</div>
            </div>
            <div className="opo-kpi">
              <div className="opo-kpi-label">AVG PRIORITY</div>
              <div className={`opo-kpi-value opo-mono-value opo-level-${wlPriorityLevel}`}>
                {wlAvgPriority.toFixed(2)}<span className="opo-kpi-sep">/</span>10
              </div>
              <div className="opo-bar-track opo-bar-mini">
                <div className={`opo-bar-fill opo-fill-${wlPriorityLevel}`} style={{ width: `${wlPriorityPct}%` }} />
              </div>
              <div className="opo-kpi-foot">priority score across queue</div>
            </div>
            <div className="opo-kpi">
              <div className="opo-kpi-label">PENDING REVIEW</div>
              <div className={`opo-kpi-value opo-mono-value opo-level-${wlPendingLevel}`}>
                {wlPending.toLocaleString()}
              </div>
              <div className="opo-bar-track opo-bar-mini">
                <div className={`opo-bar-fill opo-fill-${wlPendingLevel}`} style={{ width: `${wlPendingPct}%` }} />
              </div>
              <div className="opo-kpi-foot">{wlPendingPct.toFixed(0)}% of queue awaiting triage</div>
            </div>
          </div>
        </div>
      )}

      {/* Filters Section */}
      <div className="feedback-filters feedback-tactical-filters">
        <span className="feedback-filters-tag">FILTERS</span>
        <select 
          value={categoryFilter} 
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">All Categories</option>
          <option value="financial">💰 Financial Tools</option>
          <option value="productivity">⚡ Productivity</option>
          <option value="government">🏛️ Government Services</option>
          <option value="communication">💬 Communication</option>
          <option value="analytics">📊 Analytics & Reports</option>
          <option value="lifestyle">❤️ Lifestyle & Wellness</option>
          <option value="entertainment">🎮 Entertainment</option>
          <option value="other">💡 Other Ideas</option>
        </select>

        <select 
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">All Status</option>
          <option value="pending">Pending Review</option>
          <option value="processed">Processing</option>
          <option value="in_development">In Development</option>
          <option value="completed">Completed</option>
          <option value="rejected">Rejected</option>
        </select>

        <button onClick={() => { fetchRequests(); fetchStats(); }} className="refresh-btn">
          🔄 Refresh
        </button>
      </div>



      {/* Summary when "All Status"; request list always shown below */}
      {(!statusFilter || statusFilter === '') ? (
        <div className="summary-dashboard">
          {stats ? (
            <>
              {/* All Status */}
              <div className="summary-section">
                <h3 className="opo-section-heading">▮ STATUS BREAKDOWN</h3>
                {(() => {
                  const tiles = [
                    { key: 'pending', filterKey: 'pending', label: 'PENDING', sub: 'Awaiting Review', count: wlPending, level: 'warning' },
                    { key: 'processed', filterKey: 'processed', label: 'PROCESSING', sub: 'Admin Reviewed', count: wlProcessed, level: 'good' },
                    { key: 'in_development', filterKey: 'in_development', label: 'IN DEVELOPMENT', sub: 'Being Built', count: wlInDev, level: 'good' },
                    { key: 'completed', filterKey: 'completed', label: 'COMPLETED', sub: 'Finished', count: wlCompleted, level: 'good' },
                    { key: 'rejected', filterKey: 'rejected', label: 'REJECTED', sub: 'Not Implemented', count: wlRejected, level: 'critical' },
                  ];
                  const denom = tiles.reduce((s, t) => s + t.count, 0);
                  return (
                    <div className="status-breakdown-grid feedback-status-grid wishlist-status-grid">
                      {tiles.map(t => {
                        const pct = denom ? (t.count / denom) * 100 : 0;
                        return (
                          <div
                            key={t.key}
                            className={`status-card ${t.key} feedback-status-card`}
                            onClick={() => setStatusFilter(t.filterKey)}
                          >
                            <div className="feedback-status-card-head">
                              <span className={`opo-pip opo-pip-${t.level}`}><span className="opo-pip-core" /></span>
                              <span className="feedback-status-card-label">{t.label}</span>
                            </div>
                            <div className={`status-number opo-mono-value opo-level-${t.level}`}>{t.count}</div>
                            <div className="opo-bar-track opo-bar-mini">
                              <div className={`opo-bar-fill opo-fill-${t.level}`} style={{ width: `${pct}%` }} />
                            </div>
                            <div className="status-sublabel">{t.sub} · {pct.toFixed(0)}%</div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>

              {/* Category Analysis */}
              <div className="summary-section">
                <h3 className="opo-section-heading">▮ CATEGORY ANALYSIS</h3>
                <div className="category-analysis-grid">
                  {categories.filter(cat => cat.id !== '').map((cat) => {
                    const categoryRequests = requests.filter(req =>
                      (req.category || '').toLowerCase() === String(cat.id).toLowerCase()
                    );
                    const categoryCount = categoryRequests.length;
                    const avgPriority = categoryRequests.length > 0 
                      ? (categoryRequests.reduce((sum, req) => sum + n(req.priority_score), 0) / categoryRequests.length)
                      : 0;

                    return (
                      <div key={cat.id} className="category-analysis-card" onClick={() => setCategoryFilter(cat.id)}>
                        <div className="category-header">
                          <span className="category-name">{cat.name}</span>
                          <span className="category-count">{categoryCount} requests</span>
                        </div>
                        <div className="category-stats">
                          <div className="category-stat">
                            <span className="stat-label">% of Total:</span>
                            <span className="stat-value">{stats.total_requests > 0 ? ((categoryCount / stats.total_requests) * 100).toFixed(1) : '0.0'}%</span>
                          </div>
                          <div className="category-stat">
                            <span className="stat-label">Avg Priority:</span>
                            <span className="stat-value">{avgPriority.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            <div className="loading-stats">Loading summary data...</div>
          )}
        </div>
      ) : null}

          <div className="feedback-list">
          {(!statusFilter || statusFilter === '') && requests.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>📋 Co-Lab requests</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0, lineHeight: 1.5 }}>
                Full list of submissions (newest appear after you pick filters or scroll). Use <strong>Pending Review</strong> in the status dropdown to see only items awaiting triage.
              </p>
            </div>
          )}
          {error && (
            <div className="error-message">
              ⚠️ Error: {error}
            </div>
          )}
          
            {sortedRequests.length === 0 ? (
            <div className="no-feedback">
              🔍 No widget requests found for current filters.
              </div>
            ) : (
            sortedRequests.map((request, index) => (
                <div key={request.id} className="feedback-item">
                <details 
                  className="feedback-details"
                  onToggle={(e) => {
                    if (e.target.open) {
                      setExpandedRequest(request.id);
                    } else {
                      setExpandedRequest(null);
                    }
                  }}
                >
                  <summary className="feedback-summary">
                    {/* Request Header */}
                    <div className="compact-feedback-header">
                      {/* Left: Priority Number */}
                      <div className="priority-number-large">#{index + 1}</div>
                      
                      {/* Center: Category & Description */}
                      <div className="header-center-info">
                        <div className="category-row-expandable">
                          <span 
                            className="category-badge"
                            style={{ backgroundColor: getPriorityColor(request.priority_score || 0) }}
                          >
                            {request.category ? request.category.toUpperCase() : 'OTHER'}
                          </span>
                        </div>
                        
                        <div className="file-location-row">
                          <span className="page-context-compact">
                            💡 {request.description ? request.description.substring(0, 80) + '...' : 'Widget Request'}
                        </span>
                        </div>
                      </div>
                      
                                            {/* Right: Priority Score */}
                      <div className="priority-score-section">
                        <div className="priority-display-compact">
                          <div className="priority-score-row">
                            <div className="priority-score-large">{n(request.priority_score).toFixed(3)}</div>
                            {(statusFilter === 'pending' || request.processing_status === 'pending') && (
                              <div className="priority-adjust">
                                <button 
                                  className="priority-btn-small increase"
                                  onClick={() => handlePriorityAdjust(request.id, 0.01)}
                                  title="Increase priority by 0.01"
                                >
                                  +
                                </button>
                      <button
                                  className="priority-btn-small decrease"
                                  onClick={() => handlePriorityAdjust(request.id, -0.01)}
                                  title="Decrease priority by 0.01"
                      >
                                  -
                      </button>
                    </div>
                            )}
                          </div>
                          <div className="priority-calculation-inline">
                            <span className="calc-formula">
                              {(() => {
                                const baseScore = request.group_user_count
                                  ? n(request.group_user_count, 1) + n(request.group_avg_credibility)
                                  : 1 + n(request.user_reputation);
                                
                                const votes = {
                                  net_score: n(request.community_net_votes),
                                  upvotes: n(request.community_upvotes),
                                  downvotes: n(request.community_downvotes),
                                  total_votes: n(request.community_upvotes) + n(request.community_downvotes)
                                };
                                const adminAdjustment = n(request.admin_priority_adjustment);
                                
                                // Debug logging
                                if (request.id === 8) {
                                  console.log('DEBUG - Request 8 calculation:', {
                                    group_user_count: request.group_user_count,
                                    group_avg_credibility: request.group_avg_credibility,
                                    user_reputation: request.user_reputation,
                                    baseScore,
                                    priority_score: request.priority_score,
                                    adminAdjustment,
                                    votes
                                  });
                                }
                                
                                return (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '11px' }}>
                                    <div style={{ color: '#19EFFF' }}>
                                      {request.group_user_count ? 
                                        `${n(request.group_user_count, 1)} + ${n(request.group_avg_credibility).toFixed(2)}` :
                                        `1 + ${n(request.user_reputation).toFixed(2)}`
                                      } = {n(baseScore).toFixed(3)}
                                    </div>
                                    {votes.total_votes > 0 && (
                                      <div style={{ color: votes.net_score >= 0 ? '#4ade80' : '#f87171', fontWeight: 'bold' }}>
                                        {votes.upvotes} Users upvoted, {votes.downvotes} downvoted = {votes.net_score >= 0 ? '+' : ''}{votes.net_score.toFixed(3)}
                                      </div>
                                    )}
                                    {adminAdjustment !== 0 && (
                                      <div style={{ color: '#ffa726', fontSize: '10px' }}>
                                        Admin {adminAdjustment >= 0 ? '+' : ''}{adminAdjustment.toFixed(2)}
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </span>
                          </div>
                          {/* User Voting Summary */}
                          {console.log('[VOTE DISPLAY] Request ID:', request.id, 'Community Votes:', request.community_upvotes, request.community_downvotes, request.community_net_votes)}
                          {((request.community_upvotes || 0) + (request.community_downvotes || 0)) > 0 && (
                            <div style={{
                              background: 'rgba(25, 239, 255, 0.1)',
                              border: '1px solid rgba(25, 239, 255, 0.3)',
                              borderRadius: '6px',
                              padding: '6px 8px',
                              marginTop: '4px',
                              fontSize: '11px',
                              color: '#19EFFF'
                            }}>
                              <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
                                🗳️ Community Votes: {(request.community_upvotes || 0) + (request.community_downvotes || 0)} total
                              </div>
                              <div style={{ 
                                color: (request.community_net_votes || 0) >= 0 ? '#4ade80' : '#f87171',
                                fontWeight: 'bold'
                              }}>
                                👍 {request.community_upvotes || 0} upvoted, 👎 {request.community_downvotes || 0} downvoted
                                <span style={{ marginLeft: '8px' }}>
                                  Net: {n(request.community_net_votes) >= 0 ? '+' : ''}{n(request.community_net_votes).toFixed(3)}
                                </span>
                              </div>
                            </div>
                          )}

                          {n(request.admin_priority_adjustment) !== 0 && (
                            <div className="admin-adjustment-badge-compact">
                              Admin {n(request.admin_priority_adjustment) > 0 ? '+' : ''}{n(request.admin_priority_adjustment).toFixed(2)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </summary>

                  {/* Expanded Content */}
                  <div className="feedback-content-compact">
                    {/* Request Details */}
                    <div className="ai-summary-compact">
                      <h4>💡 Widget Request Details</h4>
                      <p><strong>Description:</strong> {request.description}</p>
                      <p><strong>Primary Category:</strong> {categories.find(c => c.id === request.category)?.name || request.category}</p>
                      <p><strong>Submitted:</strong> {formatDate(request.created_at)}</p>
                      <p><strong>User:</strong> {request.user_name} ({request.user_email})</p>
                      {request.user_reputation != null && (
                        <p><strong>User Credibility:</strong> {n(request.user_reputation).toFixed(2)}</p>
                      )}
                      
                      {/* Email Proposer Button */}
                      <div className="email-proposer-section" style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid rgba(25, 239, 255, 0.2)' }}>
                        <button 
                          className="email-proposer-btn"
                          onClick={() => handleEmailProposer(request)}
                          style={{
                            background: 'linear-gradient(135deg, #19EFFF 0%, #0ea5e9 100%)',
                            color: '#0f172a',
                            border: 'none',
                            padding: '10px 16px',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s',
                            boxShadow: '0 2px 8px rgba(25, 239, 255, 0.3)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 12px rgba(25, 239, 255, 0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 8px rgba(25, 239, 255, 0.3)';
                          }}
                        >
                          📧 Email Proposer
                        </button>
                      </div>
                    </div>

                    {/* Admin Actions */}
                    <div className="feedback-actions-compact">

                      {(request.processing_status === 'pending' || statusFilter === 'pending') ? (
                        // Pending items show full review interface
                        <div className="pending-review-section-compact">
                          {/* Category Selection */}
                          <div className="category-selector-compact">
                            <label className="section-label">
                              📂 Adjust Categories (Original: {getCategoryDisplayName(request.category)}, Selected: {getSelectedCategories(request).length}):
                            </label>
                            <div className="category-buttons-compact">
                              {editableCategories.map(cat => {
                                const isSelected = isCategorySelected(request.id, cat.id);
                                return (
                                  <button
                                    key={cat.id}
                                    className={`category-btn-compact ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleCategoryChange(request.id, cat.id)}
                                  >
                                    {cat.name}
                                  </button>
                                );
                              })}
                            </div>
                        </div>
                        
                          {/* Edit Original Request Text */}
                          <div className="developer-notes-section">
                            <label className="section-label">📝 Edit User Request Text:</label>
                            <textarea
                              placeholder="Edit the original user request description..."
                              value={pendingEdits[request.id]?.description || request.description || ''}
                              onChange={(e) => handleFieldChange(request.id, 'description', e.target.value)}
                              className="developer-notes-input"
                              rows="3"
                            />
                          </div>

                          {/* Admin Notes Section */}
                          <div className="developer-notes-section">
                            <label className="section-label">👨‍💻 Admin Notes (implementation guidance):</label>
                            <textarea
                              placeholder="Add specific instructions for implementation (e.g., 'Focus on mobile responsiveness', 'Ensure accessibility', 'Use existing component patterns')..."
                              value={pendingEdits[request.id]?.admin_notes || ''}
                              onChange={(e) => handleFieldChange(request.id, 'admin_notes', e.target.value)}
                              className="developer-notes-input"
                              rows="3"
                            />
                          </div>

                          {/* User Credibility Notice */}
                          {request.user_reputation !== undefined && (
                            <div className={`reputation-notice-compact ${n(request.user_reputation) < 1.0 ? 'requires-vetting' : 'auto-reviewable'}`}>
                              {n(request.user_reputation) < 1.0 ? (
                                <span>⚠️ User credibility {n(request.user_reputation).toFixed(2)} - Requires manual vetting</span>
                              ) : (
                                <span>✅ User credibility {n(request.user_reputation).toFixed(2)} - Trusted contributor</span>
                              )}
                          </div>
                        )}

                                                    {/* Process/Reject Buttons - Exact match to Feedback page */}
                          <div className="pending-actions-compact">
                              <button 
                              className="action-btn-compact process"
                              onClick={() => handlePendingAction(request.id, 'process')}
                              >
                              ✅ Process → Processed
                              </button>
                              <button 
                              className="action-btn-compact reject"
                              onClick={() => handlePendingAction(request.id, 'reject')}
                              >
                                ❌ Reject
                              </button>
                          </div>
                        </div>
                      ) : request.processing_status === 'processed' ? (
                        // Approved items can be marked as developed or rejected
                        <div className="approved-actions">
                          <div className="status-display">
                            <strong>Status:</strong> ✅ Approved
                          </div>
                          {request.admin_notes && (
                            <div className="admin-notes">
                              <strong>Admin Notes:</strong> {request.admin_notes}
                            </div>
                          )}
                          
                          {/* Processing Information */}
                          <div className="processing-info" style={{
                            backgroundColor: '#1a2332',
                            padding: '10px',
                            borderRadius: '6px',
                            border: '1px solid #334155',
                            marginTop: '10px'
                          }}>
                            <strong>📋 Processing Summary:</strong>
                            <p style={{margin: '5px 0'}}>• Primary Category: {categories.find(c => c.id === request.category)?.name || request.category}</p>
                            {request.admin_notes && request.admin_notes.includes('2 categories') && (
                              <p style={{margin: '5px 0'}}>• ✅ Multiple categories were selected during processing (2 categories total)</p>
                            )}
                            {request.admin_notes && request.admin_notes.includes('3 categories') && (
                              <p style={{margin: '5px 0'}}>• ✅ Multiple categories were selected during processing (3 categories total)</p>
                            )}
                            <p style={{margin: '5px 0'}}>• Status: Ready for development planning</p>
                          </div>
                          
                          {/* CBA Prompt Display */}
                          {request.cba_prompt && (
                            <div className="cba-prompt-section">
                              <strong>🤖 Generated CBA Development Prompt:</strong>
                              <div className="cba-prompt-content">
                                <pre style={{ 
                                  whiteSpace: 'pre-wrap', 
                                  fontSize: '12px', 
                                  backgroundColor: '#1a1a1a', 
                                  color: '#00ff88', 
                                  padding: '15px', 
                                  borderRadius: '8px', 
                                  border: '1px solid #333',
                                  maxHeight: '400px',
                                  overflowY: 'auto',
                                  fontFamily: 'Monaco, Consolas, monospace'
                                }}>
                                  {request.cba_prompt}
                                </pre>
                              </div>
                            </div>
                          )}
                          
                          <div className="development-actions">
                              <button 
                              className="action-btn development-btn"
                              onClick={() => {
                                if (window.confirm('Send this request to CBA LLM for development planning?')) {
                                  handleAction(request.id, 'approve', { implementation_status: 'in_progress' });
                                }
                              }}
                            >
                              🤖 Send to CBA LLM
                              </button>
                              <button 
                              className="action-btn reject-btn"
                              onClick={() => handleReject(request.id)}
                              >
                              ❌ Reject
                              </button>
                          </div>
                        </div>
                      ) : request.processing_status === 'rejected' ? (
                        // Rejected items can be deleted forever
                        <div className="rejected-section-compact">
                          <div className="status-display">
                            <strong>Status:</strong> ❌ Rejected
                          </div>
                          {request.admin_notes && (
                            <div className="admin-notes">
                              <strong>Rejection Reason:</strong> {request.admin_notes}
                            </div>
                          )}
                          <div className="danger-actions">
                            <button 
                              className="action-btn delete-btn"
                              onClick={() => handleDeleteForever(request.id)}
                            >
                              🗑️ Delete Forever
                            </button>
                          </div>
                        </div>
                      ) : request.processing_status === 'approved' && request.implementation_status === 'in_progress' ? (
                        // Enhanced In Development section with version control
                        <div className="in-development-section-enhanced">
                          <div className="status-display">
                            <strong>Status:</strong> 🚧 In Development
                          </div>
                          
                          {/* Legacy admin notes display */}
                          {getCleanAdminNotes(request.admin_notes) && (
                            <div className="legacy-admin-notes">
                              <strong>Initial Notes:</strong> {getCleanAdminNotes(request.admin_notes)}
                            </div>
                          )}
                          
                          {/* CBA Prompt Display for In Development */}
                          {request.cba_prompt && (
                            <div className="cba-prompt-section">
                              <strong>🤖 Generated CBA Development Prompt:</strong>
                              <div className="cba-prompt-content">
                                <pre style={{ 
                                  whiteSpace: 'pre-wrap', 
                                  fontSize: '12px', 
                                  backgroundColor: '#1a1a1a', 
                                  color: '#00ff88', 
                                  padding: '15px', 
                                  borderRadius: '8px', 
                                  border: '1px solid #333',
                                  maxHeight: '400px',
                                  overflowY: 'auto',
                                  fontFamily: 'Monaco, Consolas, monospace',
                                  marginTop: '10px'
                                }}>
                                  {request.cba_prompt}
                                </pre>
                              </div>
                            </div>
                          )}
                          
                          {/* Enhanced Version Control Component */}
                          <WidgetVersionControl 
                            widgetId={request.id}
                            currentStatus={request.implementation_status}
                            onVersionUpdate={(version) => {
                              console.log('Version updated:', version);
                              // Refresh the requests data to show updated status
                              fetchRequests();
                            }}
                          />
                        </div>
                      ) : (
                        // Other statuses (completed, in_development, etc.)
                        <div className="processed-section-compact">
                          <div className="status-display">
                            <strong>Status:</strong> {request.status}
                          </div>
                          {request.admin_notes && (
                            <div className="admin-notes">
                              <strong>Admin Notes:</strong> {request.admin_notes}
                            </div>
                          )}
                          {request.implementation_status && (
                            <div className="implementation-status">
                              <strong>Implementation:</strong> {request.implementation_status}
                            </div>
                          )}
                        </div>
                      )}
                      </div>
                  </div>
                </details>
                </div>
              ))
            )}

          {/* Show total items */}
          <div className="all-items-info">
            <div className="total-items-display">
              <strong>📋 Showing {sortedRequests.length} widget requests</strong>
            </div>
          </div>
        </div>

      {/* Email Modal */}
      <EmailModal
        isOpen={emailModalOpen}
        onClose={() => {
          setEmailModalOpen(false);
          setSelectedWidgetRequest(null);
        }}
        widgetRequest={selectedWidgetRequest}
      />
    </div>
  );
};

// Add CSS styles for CBA Processing Animation
const styleElement = document.createElement('style');
styleElement.textContent = `
  .cba-processing-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10000;
  }

  .cba-processing-modal {
    background: rgba(26, 26, 46, 0.95);
    border-radius: 12px;
    padding: 30px;
    text-align: center;
    border: 1px solid rgba(0, 255, 136, 0.3);
    animation: fadeIn 0.3s ease-out;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .optiprime-logo-simple {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }

  .logo-text {
    font-size: 1.8em;
    font-weight: bold;
    color: #00ff88;
    margin: 0;
  }

  .processing-dots-simple {
    display: flex;
    justify-content: center;
    gap: 6px;
  }

  .processing-dots-simple span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #00ff88;
    animation: dotBlink 1.2s ease-in-out infinite both;
  }

  .processing-dots-simple span:nth-child(1) { animation-delay: -0.4s; }
  .processing-dots-simple span:nth-child(2) { animation-delay: -0.2s; }
  .processing-dots-simple span:nth-child(3) { animation-delay: 0s; }

  @keyframes dotBlink {
    0%, 80%, 100% { 
      opacity: 0.3;
    } 
    40% { 
      opacity: 1;
    }
  }
`;

if (!document.head.querySelector('[data-cba-processing-styles]')) {
  styleElement.setAttribute('data-cba-processing-styles', 'true');
  document.head.appendChild(styleElement);
}

export default WishListManager;